import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import {OrderStatus} from '../types';
import { useMerchant } from '../context/MerchantContext';

const StatusTimeline = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 2rem;
  margin-bottom: 3rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    height: 4px;
    background-color: #eee;
    z-index: 1;
  }
`;

const TimelineStep = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
  flex: 1;
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.completed ? '#2ecc71' : props.active ? '#e74c3c' : '#eee'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
`;

const StepLabel = styled.span<{ active: boolean }>`
  font-size: 0.8rem;
  color: ${props => props.active ? '#333' : '#999'};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 1rem;
  max-width: 800px;
  margin: 0 auto;
`;

const SuccessIcon = styled.div`
  font-size: 5rem;
  color: #2ecc71;
  margin-bottom: 2rem;
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.p`
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const OrderInfo = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  margin-bottom: 2rem;
`;

const InfoTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: bold;
  color: #333;
`;

const InfoValue = styled.span`
  color: #666;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(Link)`
  background-color: #e74c3c;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #c0392b;
  }
`;

const SecondaryButton = styled(Link)`
  background-color: #f1f1f1;
  color: #333;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const OrderSuccessPage: React.FC = () => {
  const [currentStatus, setCurrentStatus] = useState<OrderStatus>(OrderStatus.RECEIVED);
  const { setHasSeenSuggestions } = useMerchant();
  
  // Clear the suggestions flag when the order is successfully placed
  useEffect(() => {
    setHasSeenSuggestions(false);
  }, [setHasSeenSuggestions]);

  // Simulation of order status updates
  useEffect(() => {
    const statuses = [
      OrderStatus.RECEIVED,
      OrderStatus.CONFIRMED,
      OrderStatus.ACCEPTED,
      OrderStatus.READY_FOR_TAKEAWAY,
      OrderStatus.DELIVERED
    ];
    
    let index = 0;
    const interval = setInterval(() => {
      index++;
      if (index < statuses.length) {
        setCurrentStatus(statuses[index]);
      } else {
        clearInterval(interval);
      }
    }, 10000); // Update status every 10 seconds for demo
    
    return () => clearInterval(interval);
  }, []);

  const getStatusIndex = (status: OrderStatus) => {
    const statuses = [
      OrderStatus.RECEIVED,
      OrderStatus.CONFIRMED,
      OrderStatus.ACCEPTED,
      OrderStatus.READY_FOR_TAKEAWAY,
      OrderStatus.DELIVERED
    ];
    return statuses.indexOf(status);
  };

  const statusIndex = getStatusIndex(currentStatus);
  const orderId = "ORD" + Math.floor(Math.random() * 10000);
  
  const steps = [
    { label: 'Pendente', status: OrderStatus.RECEIVED },
    { label: 'Confirmado', status: OrderStatus.CONFIRMED },
    { label: 'Confirmado', status: OrderStatus.CANCELLED_BY_TIMEOUT },
    { label: 'Em Preparo', status: OrderStatus.ACCEPTED },
    { label: 'Pronto', status: OrderStatus.READY_FOR_TAKEAWAY },
    { label: 'Entregue', status: OrderStatus.DELIVERED },
  ];
  
  return (
    <Layout>
      <SuccessContainer>
        <SuccessIcon>✅</SuccessIcon>
        <SuccessTitle>Pedido Realizado com Sucesso!</SuccessTitle>
        <SuccessMessage>
          Seu pedido foi recebido e está sendo processado. Você receberá atualizações sobre o status do seu pedido.
        </SuccessMessage>

        <StatusTimeline>
          {steps.map((step, index) => (
            <TimelineStep 
              key={step.status} 
              active={statusIndex === index}
              completed={statusIndex > index}
            >
              <StepCircle 
                active={statusIndex === index}
                completed={statusIndex > index}
              >
                {statusIndex > index ? '✓' : index + 1}
              </StepCircle>
              <StepLabel active={statusIndex === index}>{step.label}</StepLabel>
            </TimelineStep>
          ))}
        </StatusTimeline>
        
        <OrderInfo>
          <InfoTitle>Informações do Pedido</InfoTitle>
          <InfoRow>
            <InfoLabel>Número do Pedido:</InfoLabel>
            <InfoValue>{orderId}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Data:</InfoLabel>
            <InfoValue>{new Date().toLocaleDateString('pt-BR')}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Status:</InfoLabel>
            <InfoValue>Recebido</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Tempo Estimado:</InfoLabel>
            <InfoValue>30-45 minutos</InfoValue>
          </InfoRow>
        </OrderInfo>
        
        <SuccessMessage>
          Agradecemos pela sua preferência! Se tiver alguma dúvida sobre seu pedido, entre em contato conosco.
        </SuccessMessage>
        
        <ButtonsContainer>
          <PrimaryButton to="/menu">Fazer Outro Pedido</PrimaryButton>
          <SecondaryButton to="/">Voltar para Home</SecondaryButton>
        </ButtonsContainer>
      </SuccessContainer>
    </Layout>
  );
};

export default OrderSuccessPage;